import { assembleRetrievalBundle } from './retrievalAssembly'
import { compressRetrievalRecords } from './retrievalCompression'
import {
  createRetrievalCacheDocument,
  retrievalCacheKey,
} from './retrievalCache'
import { rankRetrievalRecords } from './retrievalRanking'
import { DEFAULT_RETRIEVAL_SCOPE_PROVIDERS } from './retrievalScopes'
import { hashDiagnosticIdentifier, recordRetrievalTrace } from '../../diagnostics/operationalDiagnostics'
import type {
  AiRetrievalBundle,
  AiRetrievalEngineInput,
  AiRetrievalScope,
} from './retrievalTypes'

export const DEFAULT_RETRIEVAL_SCOPES: AiRetrievalScope[] = [
  'timeline',
  'checklist_graph',
  'workspace_pulse',
  'cognitive_memory',
  'governance_activity',
]

const bundleFromCache = (
  input: AiRetrievalEngineInput,
  cacheKey: string,
  _scopes: AiRetrievalScope[],
  startedAt: number,
): AiRetrievalBundle | null => {
  if (!input.cache) return null
  const nowIso = input.nowIso ?? new Date().toISOString()
  const cached = input.cache.get(cacheKey, nowIso)
  if (!cached) return null
  const diagnostics = {
    ...cached.diagnostics,
    generatedAtIso: nowIso,
    elapsedMs: Math.max(0, Date.now() - startedAt),
    cache: {
      key: cacheKey,
      hit: true,
      writeAttempted: false,
      stale: false,
    },
  }
  return {
    workspaceId: input.workspaceId,
    queryHash: cached.queryHash,
    queryPreview: input.query.slice(0, 120),
    digest: {
      ...cached.digest,
      retrievalDiagnostics: diagnostics,
      generatedAtIso: nowIso,
    },
    slices: cached.slices,
    rankedRecords: [],
    diagnostics,
  }
}

const emitRetrievalDiagnostics = (bundle: AiRetrievalBundle) => {
  const compressionRatio = bundle.diagnostics.compression.rawCharacterCount > 0
    ? bundle.diagnostics.compression.compressedCharacterCount / bundle.diagnostics.compression.rawCharacterCount
    : 1
  void recordRetrievalTrace({
    traceId: `retrieval_${bundle.queryHash}_${bundle.diagnostics.generatedAtIso}`,
    workspaceId: bundle.workspaceId,
    queryHash: bundle.queryHash,
    elapsedMs: bundle.diagnostics.elapsedMs,
    sourceCounts: bundle.diagnostics.sourceCounts,
    selectedScopes: bundle.diagnostics.selectedScopes,
    rankedRecordCount: bundle.diagnostics.rankedRecordCount,
    includedRecordCount: bundle.diagnostics.includedRecordCount,
    omittedRecordCount: bundle.diagnostics.omittedRecordCount,
    compression: {
      ...bundle.diagnostics.compression,
      ratio: compressionRatio,
    },
    cache: bundle.diagnostics.cache,
    topRanking: bundle.diagnostics.topRanking.slice(0, 5).map((entry) => ({
      ...entry,
      id: hashDiagnosticIdentifier(entry.id) ?? 'h_unknown',
    })),
    degradationReasons: [
      bundle.diagnostics.includedRecordCount === 0 ? 'no_context_records' : '',
      bundle.diagnostics.elapsedMs > 1500 ? 'slow_retrieval' : '',
      compressionRatio < 0.08 ? 'heavy_compression' : '',
    ].filter(Boolean),
    createdAtIso: bundle.diagnostics.generatedAtIso,
  })
}

export const buildOperationalRetrievalContext = (input: AiRetrievalEngineInput): AiRetrievalBundle => {
  const startedAt = Date.now()
  const scopes = input.requestedScopes?.length ? input.requestedScopes : DEFAULT_RETRIEVAL_SCOPES
  const cacheKey = retrievalCacheKey(input, scopes)
  const cached = bundleFromCache(input, cacheKey, scopes, startedAt)
  if (cached) {
    emitRetrievalDiagnostics(cached)
    return cached
  }

  const providerInput = { ...input, requestedScopes: scopes }
  const records = DEFAULT_RETRIEVAL_SCOPE_PROVIDERS
    .filter((provider) => scopes.includes(provider.scope))
    .flatMap((provider) => provider.retrieve(providerInput))
  const ranked = rankRetrievalRecords(records, input)
  const maxRecords = input.limits?.maxRecords ?? 20
  const maxSlices = input.limits?.maxSlices ?? 8
  const maxCharacters = input.limits?.maxCharacters ?? 4_800
  const slices = compressRetrievalRecords(ranked.slice(0, maxRecords), { maxSlices, maxCharacters })
  const bundle = assembleRetrievalBundle({
    request: input,
    scopes,
    rankedRecords: ranked,
    slices,
    startedAt,
    cacheKey,
    cacheHit: false,
    cacheWriteAttempted: Boolean(input.cache),
  })

  try {
    const document = createRetrievalCacheDocument(bundle, cacheKey, scopes, input.nowIso ?? new Date().toISOString())
    input.cache?.set(document)
  } catch {
    // Cache writes are best-effort; retrieval must never block AI execution.
  }

  emitRetrievalDiagnostics(bundle)
  return bundle
}
